// R5.2 — LAYER 5: the specialty relevance overlay (spec §4.5).
//
// SINGLE SNAPSHOT AUTHORITY. The overlay is EMBEDDED in the read payload, computed and pinned with it. There is
// no second endpoint, so there is no snapshot/profile-cycle disagreement to reconcile: what the read row stored
// is what renders, and a profile-release rollover makes the read STALE (`stale_reason` includes
// `overlay_release`) rather than silently re-resolving it under a release it was never computed against.
//
// SINGLE COPY AUTHORITY (ruling R5-OQ-E). `prevalence` and `documentation` are the engine's own `RenderedString`s
// — `{copy_string_id, text}` — rendered VERBATIM. `renderPrevalence` filled `{criterion}`/`{n}`/`{N}` from the
// owner-reviewed profile release at compute time, so the criterion label is already inside `prevalence.text`.
// There is NO client-side re-fill, NO `CS-PREVALENCE-*` template the client fills, and NO `PC-*` re-resolution:
// `profile_copy` is a DEFINED but DORMANT mechanism in R5 (no served field is typed for it).
//
// NO PROGRAM MODE (R5-OQ-B). `mode` has no `"program"` value, `CS-PROG-SUMMARY` is rendered by nothing here, and
// there is no program route, payload slice, or drill-down affordance anywhere in this component.
//
// ROW ORDER IS THE PROFILE'S FIXED `CT-A` CRITERION ORDER — a property of the ratified vocabulary, never of the
// corpus and never of the CV. This component renders `overlay.rows` in payload order and sorts nothing
// (T-ORDER-1's overlay extension; §12.4 additionally bans ordering criteria by whether the CV documents them).
//
// ZERO RELEASED PROFILES TODAY. `specialtyProfiles.json` ships zero profiles, so the live path through this panel
// is the generic-mode notice — which is exactly why "renders only the generic-mode notice when no released
// profile exists" is R5.2's stated overlay scope, and why the row-rendering half is pinned here against fixture
// profiles rather than left for R5.4 to discover.
import type { OverlayStatus, RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import { BandChip, Copy, EngineRendered, EvidenceReveal, LayerHeading, layerHeadingId } from './renderContent';
import { resolveEvidence, type EvidenceIndex } from './evidenceIndex';
import { scanSystemText } from './bannedOutput';
import { ConventionReextractNotice, SpecialtyConventionPanel, conventionPanelHasRows, type ConventionReextractState } from './SpecialtyConventionPanel';
import { BODY_STYLE, CARD_STYLE, MUTED_STYLE, NOTICE_STYLE, SECTION_STYLE } from './styles';

type Overlay = NonNullable<RubricReadPublicV1['overlay']>;

/**
 * Does the panel additionally render `CS-PROFILE-EXPIRED`?
 *
 * ONE CONDITION, AND IT IS A PAYLOAD FACT RATHER THAN AN INFERENCE (ruling R5-OQ-H, spec rev 14). §4.5
 * distinguishes two causes of generic mode — "a profile that expired/rolled over" vs "a specialty that has never
 * had a released profile" — and asks for this notice only in the first. The payload now CARRIES that distinction:
 * `overlay_status` is derived at SERVE time (rev-14 amendment) from the read's own pinned `overlay_release_id`
 * against the release registry as it stands now, and `'expired'` means exactly §6.6's case, a profile that exists
 * for this specialty whose cycle has turned.
 *
 * IT IS CHECKED IN BOTH MODES (R5.2 round-2 finding 2). The case §4.5 actually describes is a read COMPUTED in
 * specialty mode whose profile has since expired: its stored `mode` is `'specialty'` forever, because mode is a
 * fact about the compute. Checking the notice only inside the generic branch made that case — the one the
 * tri-state exists for — unrenderable. The panel still renders its stored snapshot in that state (§4.5's single
 * snapshot authority: what the row stored is what renders); the notice rides ABOVE it, saying why the criteria
 * below are from a release that no longer applies.
 *
 * WHAT THIS REPLACES, AND WHY THE OLD TEST WAS WRONG IN BOTH DIRECTIONS. The previous condition was
 * `mode === 'generic' && stale_reason.includes('overlay_release')`. A FIRST-EVER release moves that same manifest
 * component, so a generic read taken before any profile existed rendered a notice claiming criteria "previously
 * shown" for this specialty had expired — criteria that were never shown. And an actually-expired read keeps its
 * stored `mode: 'specialty'`, so the case the spec asks for never rendered at all. `stale_reason` answers "did
 * the inputs move", which is a different question from "did this specialty's profile expire", and only the second
 * one licenses this sentence.
 *
 * WITH ZERO RELEASED PROFILES the status is `null` on every read and this notice renders nowhere — the correct
 * state for a product whose `specialtyProfiles.json` ships empty, not a gap.
 */
export function showsProfileExpired(overlay_status: OverlayStatus | null): boolean {
  return overlay_status === 'expired';
}

/**
 * T-BAN-2s AT ROW GRANULARITY (R5.4 — the gap the poisoned-LABEL fixture found in the production path).
 *
 * WHAT WAS WRONG. Every system-authored string in a row was already scanned — `EngineRendered` scans the
 * engine's `RenderedString`s, `TemplateText` scans `CS-OVERLAY-SOURCE`'s `profile_release_id` slot — and each
 * one returns `null` on a violation. That blocks the offending STRING. It does not block the ROW, so a poisoned
 * criterion label left a criterion card on the screen carrying its documentation verdict, its closed-world
 * scope and its evidence disclosure with the sentence that names WHAT is being claimed silently missing. A
 * verdict with its subject removed is not the safer render; it is the same defect T-BAN-2s already names one
 * level down ("half a sentence is not a safer sentence"), at the level the overlay actually composes.
 *
 * WHY THE ROW AND NOT THE PANEL. §7's own poisoned-`profile_release_id` fixture fixes the granularity: the
 * violating value must "render NOWHERE on the surface, with the SIBLING ROWS UNAFFECTED". So a violation takes
 * its whole row and stops there — the hedge, the non-documentable note and every clean row still render, which
 * is also what keeps §6.4 H1's mandatory hedge from being suppressible by one bad criterion.
 *
 * WHY IT IS A PRE-PASS. The scan runs BEFORE any of the row is emitted, for the same reason `TemplateText`
 * scans every slot up front: a violation in the LAST field must not leave the first three already on screen.
 *
 * THE SCANNED SET IS EXACTLY THE ROW'S SYSTEM-AUTHORED STRINGS. The two engine `RenderedString`s (which carry
 * the criterion label and any partial-documentation reason inside them, R5-OQ-E) and the `profile_release_id`
 * that fills `CS-OVERLAY-SOURCE`. The registered `CS-*` ids (`CS-MAPPING-UNCERTAIN`, `CS-OMISSION-SCOPE`) are
 * scanned by `Copy` itself and are registry members by construction; the omission count is a number; the
 * evidence spans are `applicant_verbatim` and exempt by §12.6 part 0. Nothing here is scanned twice as a
 * substitute for being scanned once at its own chokepoint — those scans all remain.
 */
export function rowRenders(row: Overlay['rows'][number]): boolean {
  return (
    scanSystemText(row.prevalence.text, row.prevalence.copy_string_id).ok
    && scanSystemText(row.documentation.text, row.documentation.copy_string_id).ok
    && scanSystemText(row.profile_release_id, 'CS-OVERLAY-SOURCE').ok
  );
}

export function SpecialtyOverlayPanel({
  overlay,
  mode,
  overlay_status,
  evidence,
  convention = null,
  reextract = null,
}: {
  overlay: Overlay | null;
  mode: 'generic' | 'specialty';
  overlay_status: OverlayStatus | null;
  evidence: EvidenceIndex;
  /**
   * R7.1 §6b — the specialty CONVENTION panel, rendered in THIS slot, above whatever else the slot carries.
   *
   * It is a different claim from the overlay beside it (cited national standards vs. published-description
   * prevalence) and therefore a separate panel with its own hedge, sources and footer — never merged into an
   * overlay row and never rendered as a mode of one. Null (the ordinary state: the layer is off, or the read
   * predates R7.1) renders nothing at all and leaves this slot byte-identical to before.
   */
  convention?: RubricReadPublicV1['convention'] | null;
  /**
   * R7.2 — the §6b signals re-extraction wait / retry. It rides in THIS slot with the panel it is about, and is
   * rendered here on its own when there is no `convention` payload to hang it under: a failed pass with no visible
   * retry is the state this change exists to end, so the affordance never depends on a payload it does not need.
   */
  reextract?: ConventionReextractState | null;
}) {
  // GENERIC MODE — ONE BEHAVIOUR, CHOSEN (§4.5, finding 10 of the third-pass review). The notice renders whenever
  // no released profile applies, FOR ANY REASON. There is no silent empty panel and no "coming soon".
  if (overlay === null || mode === 'generic') {
    // R7.3 — THE CONTRADICTION FIX. `CS-GENERIC-MODE-NOTICE`'s flat "no specialty-specific criteria are applied"
    // reads as a self-contradiction directly under a §6b convention panel that just rendered rows. Computed from
    // the SAME predicate the convention panel itself uses for section A (`conventionPanelHasRows`, which reuses
    // `conventionRowRenders` — never duplicated logic), never from `mode` or `overlay` alone, because those say
    // nothing about whether section A actually had rows.
    const hasConventionRows = conventionPanelHasRows(convention ?? null);
    return (
      <>
      {convention == null && <ConventionReextractNotice state={reextract} />}
      <SpecialtyConventionPanel convention={convention ?? null} evidence={evidence} reextract={reextract} />
      <section
        aria-labelledby={layerHeadingId('CS-SECTION-OVERLAY')}
        style={SECTION_STYLE}
        data-testid="rubric-layer-overlay"
      >
        <LayerHeading id="CS-SECTION-OVERLAY" />
        {hasConventionRows ? (
          <p style={NOTICE_STYLE} data-testid="rubric-convention-scope-notice">
            <Copy id="CS-CONVENTION-SCOPE-NOTICE" />
          </p>
        ) : (
          <p style={NOTICE_STYLE} data-testid="rubric-generic-mode-notice">
            <Copy id="CS-GENERIC-MODE-NOTICE" />
          </p>
        )}
        {showsProfileExpired(overlay_status) && (
          <p style={NOTICE_STYLE} data-testid="rubric-profile-expired">
            {/* Worded to say SPECIALTY, never program — CVCraft has no program mode (R5-OQ-B). */}
            <Copy id="CS-PROFILE-EXPIRED" />
          </p>
        )}
      </section>
      </>
    );
  }

  return (
    <>
      {convention == null && <ConventionReextractNotice state={reextract} />}
      <SpecialtyConventionPanel convention={convention ?? null} evidence={evidence} reextract={reextract} />
      {/* SPECIALTY MODE, EXPIRED PROFILE — §4.5's own case (round-2 finding 2). The snapshot below still renders
          verbatim (it is what this read stored), and this notice rides above the panel rather than inside it, so
          §6.4 H1's "the hedge persists at panel top" stays literally true. */}
      {showsProfileExpired(overlay_status) && (
        <p style={NOTICE_STYLE} data-testid="rubric-profile-expired">
          {/* Worded to say SPECIALTY, never program — CVCraft has no program mode (R5-OQ-B). */}
          <Copy id="CS-PROFILE-EXPIRED" />
        </p>
      )}
      {/* THE SECTION'S NAME IS ITS REGISTERED HEADING (accessibility audit A11Y-1, round 3), not the hedge.
          The hedge is unchanged and still the first thing INSIDE the panel — §6.4 H1's "persists at panel
          top" is about where it renders, not about what names the region — and the GENERIC branch above
          now carries the same heading, so the overlay layer is findable whether or not a profile applied. */}
      <section
        aria-labelledby={layerHeadingId('CS-SECTION-OVERLAY')}
        style={SECTION_STYLE}
        data-testid="rubric-layer-overlay"
      >
      <LayerHeading id="CS-SECTION-OVERLAY" />
      {/* §6.4 H1 — the hedge is MANDATORY and persists at panel top. Not nullable, not dismissable. */}
      <p id="rubric-overlay-hedge" style={NOTICE_STYLE} data-testid="rubric-overlay-hedge">
        <Copy id={overlay.hedge_copy_string_id} />
      </p>
      {/* A.5-P.11 — Sol's recall disclosure (owner rule for completing already-approved scope, 2026-08-21).
          `CS-OVERLAY-HEDGE` above says the criteria state what programs NAME, not how they WEIGH it — an
          interpretation hedge. It says nothing about RECALL: detection runs over public program descriptions
          and is a lower bound, so a criterion or a whole program can be missed entirely. This renders ONCE,
          panel-wide, directly under the hedge, specialty mode only — a `copy`-provenance body-text string,
          never a chip. Fixed render order at panel top: hedge, then this disclosure, then the criterion rows. */}
      <p style={NOTICE_STYLE} data-testid="rubric-overlay-recall-disclosure">
        <Copy id="CS-OVERLAY-RECALL-DISCLOSURE" />
      </p>

      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {overlay.rows.filter(rowRenders).map((row) => (
          <li key={row.criterion_id} style={CARD_STYLE} data-criterion-id={row.criterion_id}>
            <p style={BODY_STYLE}>
              <EngineRendered value={row.prevalence} />
            </p>
            {/* THE DOCUMENTATION CLASS, UNDER THE UNIFORM BAND TREATMENT (§1.4, T-UNIFORM-1; spec rev 14).
                This row's `documentation_class` is the overlay's band-like value — the per-criterion statement
                about what THIS CV documents — and it renders through the same `BandChip` as every content band,
                presentation band and facet level, so the computed-style sweep reaches the overlay instead of
                stopping at the layers that happened to use a chip.

                THE LABEL IS THE ENGINE'S OWN RENDERED CLASS STRING, verbatim (R5-OQ-E): `documentation` is a
                `RenderedString` the engine already filled from the owner-reviewed release, carrying its own
                registered `CS-DOC-*` id. Appendix A registers no separate short label for the three classes, and
                this surface invents none — so the chip carries the class's own registered rendering and nothing
                else. The row's remaining prose (prevalence, the mapping-certainty hedge, the closed-world scope,
                the source reference) stays body text, exactly as before. */}
            <p style={BODY_STYLE} data-documentation-class={row.documentation_class}>
              <BandChip value={row.documentation} />
            </p>
            {row.mapping_certainty_copy_string_id && (
              <p style={BODY_STYLE} data-testid="rubric-mapping-uncertain">
                <Copy id={row.mapping_certainty_copy_string_id} />
              </p>
            )}
            {/* C2b — a `none` row's omission claim discloses the closed world it was assessed over, through the
                registered `CS-OMISSION-SCOPE` template. The raw `scope_description` is audit-only and never
                renders (R5-10P-05). */}
            {row.search_scope && row.search_scope.n_assessed > 0 && (
              <p style={MUTED_STYLE} data-testid="rubric-overlay-omission-scope">
                <Copy id="CS-OMISSION-SCOPE" slots={{ n: String(row.search_scope.n_assessed) }} />
              </p>
            )}
            <EvidenceReveal evidence={resolveEvidence(evidence, row.evidence_fact_ids)} />
            {/* EVERY ROW CARRIES ITS SOURCE REFERENCE, naming the row's OWN pinned release — never "the active
                release", which is the whole point of pinning it per row (§4.5, §11 item 4). */}
            <p style={MUTED_STYLE} data-testid="rubric-overlay-source">
              <Copy id="CS-OVERLAY-SOURCE" slots={{ profile_release_id: row.profile_release_id }} />
            </p>
          </li>
        ))}
      </ol>

      {overlay.non_documentable_note_copy_string_id && (
        <p style={NOTICE_STYLE} data-testid="rubric-non-documentable">
          <Copy id={overlay.non_documentable_note_copy_string_id} />
        </p>
      )}
      </section>
    </>
  );
}
